import { DOCUMENT } from '@angular/common';
import { computed, inject, InjectionToken, OnDestroy, Signal } from '@angular/core';
import { ControlConfig } from '@angular/forms';
import { Params } from '@angular/router';
import * as fromRouter from '@ngrx/router-store';
import { Subject } from 'rxjs';
import { toKml } from './tokml';


type Falsy = false | 0 | '' | null | undefined

export const truthy = <T>(x: T | Falsy): x is T | 0 => !!x || x === 0;

export const exists = <T>(x: T | Falsy): x is T => !!x;

/**
 * operator `%` is reminder in javascript, we usually want modulo (e.g. while working with negative array indices)
*/
export const modulo = (n: number, d: number) => ((n % d) + d) % d;

/**
 * exposes Document.Location via InjectionToken
 */
export const LocationToken: InjectionToken<Location> = new InjectionToken('Location API', {
    factory: (): Location => inject(DOCUMENT).location,
    providedIn: 'root',
});


export const unique = <T>(xs: T[], id: (x: T) => unknown = x => x) => xs.filter((x, i) => xs.findIndex(y => id(x) === id(y)) === i);


export const isEqual = <T>(first: T, second: T): boolean => {
    if (first === second) {
        return true;
    }
    if (Array.isArray(first) && Array.isArray(second)) {
        if (first.length !== second.length) {
            return false;
        }
        return first.every((value, index) => isEqual(value, second[index]));
    }
    if (typeof first === 'object' && typeof second === 'object' && !Array.isArray(first) && !Array.isArray(second)) {
        if (first === null || second === null) {
            return false;
        }
        const keys = new Set([...Object.keys(first), ...Object.keys(second)]);
        for (const key of Array.from(keys)) {
            if (!isEqual(first[key as keyof typeof first], second[key as keyof typeof second])) {
                return false;
            }
        }
        return true;
    }
    return false;
};


export const diff = <T extends object | null, U>(first: T, second: U): Partial<T> => {
    if (first === null) {
        return second || {};
    }
    const result = {};
    if (Object.is(first, second)) {
        return {};
    }
    Object.keys(first).concat(Object.keys(second || {})).forEach(key => {
        // @ts-ignore
        if (second[key] !== first[key] && typeof second[key] !== 'undefined' && !Object.is(first[key], second[key])) {
            // @ts-ignore
            result[key] = second[key];
        }
        // @ts-ignore
        if (typeof second[key] === 'object' && typeof first[key] === 'object') {
            // @ts-ignore
            const value = diff(first[key], second[key]);
            if (Object.keys(value).length > 0) {
                // @ts-ignore
                result[key] = value;
            }
        }
    });
    return result;
};


export const computedPrevious = <T>(s: Signal<T>, startsWith: T): Signal<T> => {
    let current = startsWith;
    let previous = startsWith;

    return computed(() => {
        const value = s();
        if (value !== current) {
            previous = current;
            current = value;
        }
        return previous;
    });
};


export const computedDiff = <T, K>(value: Signal<T[]>, id: (x: T) => K | T = x => x) => {
    const previous = computedPrevious(value, []);

    return computed(() => {
        const curr = value();
        const prev = previous();

        return {
            forEachInsert: (fn: (value: T, index: number) => void) => {
                const prevSet = new Set(prev.map(id));
                for (let i = 0; i < curr.length; i++) {
                    const value = curr[i];
                    if (!prevSet.has(id(value))) {
                        fn(value, i);
                    }
                }
            },
            forEachRemove: (fn: (value: T, index: number) => void) => {
                const currSet = new Set(curr.map(id));
                for (let i = 0; i < prev.length; i++) {
                    const value = prev[i];
                    if (!currSet.has(id(value))) {
                        fn(value, i);
                    }
                }
            },
            forEachChange: (fn: (value: T) => void) => {
                const prevMap = new Map(prev.map(v => [id(v), v]));
                for (const value of curr) {
                    const i = id(value);
                    if (prevMap.has(i) && value != prevMap.get(i)) {
                        fn(value);
                    }
                }
            },
        };
    });
};


interface LoadScriptOptions {
    onload?: () => void;
    async?: boolean;
}

export function loadScript(src: string, options?: LoadScriptOptions) {
    const node = document.createElement('script');
    node.type = 'text/javascript';
    node.src = src;
    if (options && options.onload) {
        node.onload = options.onload;
    }
    if (!options || options.async !== false) {
        node.async = true;
    }
    document.body.appendChild(node);
}

export function loadStyles(href: string) {
    const node = document.createElement('link');
    node.rel = 'stylesheet';
    node.type = 'text/css';
    node.href = href;
    document.head.appendChild(node);
}

export const _localStorage = new class implements Partial<Storage> {
    memoryStorage: { [key: string]: string | null } = {};

    _useMemoryStorage?: boolean;

    protected get useMemoryStorage() {
        if (typeof this._useMemoryStorage === 'undefined') {
            try {
                const key = '__test_storage__';
                localStorage.setItem(key, '1');
                localStorage.removeItem(key);
                this._useMemoryStorage = false;
            } catch (e) {
                // Safari < 11 in Private Browsing Mode looks like it supports localStorage but all calls to setItem
                // throw QuotaExceededError
                console.warn(e);
                this._useMemoryStorage = true;
            }
        }
        return this._useMemoryStorage;
    }

    getItem(key: string): string | null {
        if (this.useMemoryStorage) {
            return this.memoryStorage[key];
        }
        return localStorage.getItem(key);
    }

    setItem(key: string, data: string) {
        if (this.useMemoryStorage) {
            this.memoryStorage[key] = data;
            return;
        }
        localStorage.setItem(key, data);
    }
}();

/**
 * LocalStorage for storing string values
 */
export function LocalStorage(key?: string, defaultValue: any = null) {
    /**
     * @param target: The prototype of the class
     * @param decoratedPropertyName: The name of the property
     */
    return (target: object, decoratedPropertyName: string) => {
        const propNameId = key ? key : decoratedPropertyName;

        Object.defineProperty(target, decoratedPropertyName, {
            get: () => {
                const item = _localStorage.getItem(propNameId);
                try {
                    return typeof item === 'string' ? JSON.parse(item) : defaultValue;
                } catch {
                    console.error(`cannot read data from LS (${propNameId})`);
                    return defaultValue;
                }
            },
            set: newValue => {
                _localStorage.setItem(propNameId, JSON.stringify(newValue));
            },
        });
    };
}

/**
 * triggers Save As dialog or saves file to disk based on browser's preferences
 * if it is not working in some browser we wan't to support, take a look here: http://danml.com/download.html
 */
export const download = (file: Blob | string, filename: string) => {
    const element = document.createElement('a');
    element.style.display = 'none';

    let url: any;
    if (file instanceof Blob) {
        url = window.URL.createObjectURL(file);
    } else {
        url = file;
        // if we have URL - ensure there is target="_blank" thing
        element.setAttribute('target', '_blank');
    }
    element.href = url;
    element.setAttribute('download', filename);

    // Safari thinks _blank anchor are pop ups. We only want to set _blank
    // target if the browser does not support the HTML5 download attribute.
    // This allows you to download files in desktop safari if pop up blocking
    // is enabled.
    if (typeof element.download === 'undefined') {
        element.setAttribute('target', '_blank');
    }

    document.body.appendChild(element);
    element.click();
    // document.body.removeChild(element);
    setTimeout(() => {
        // For Firefox it is necessary to delay revoking the ObjectURL
        window.URL.revokeObjectURL(url);
    }, 100);
};

export const exportCsv = (headers: string[], rows: string[][], filename: string) => {
    const cellDelimiter = ',';
    const lineDelimiter = '\n';

    // strip tags and escape quote for double quotes (" => "")
    const element = document.createElement('span');
    const escape = (value: string) => {
        element.innerHTML = `${value}`;
        return '"' + `${element.innerText}`.replace(/"/g, '""') + '"';
    };

    const lines = [headers, ...rows].map(line => line.map(escape).join(cellDelimiter));
    const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);  // UTF-8 BOM for forcing UTF-8 encoding in Excel
    download(new Blob([bom, lines.join(lineDelimiter)], { type: 'text/csv; charset=utf-8' }), filename);
};

export const exportKml = (geoJson: any) => {
    const name = `Sites-${new Date().toLocaleString()}`;
    const kml = toKml(geoJson, {
        name: 'Name',
        documentName: name,
        documentDescription: 'Generated by AUTOPROP.ca',
    });
    download(new Blob([kml], { type: 'application/vnd.google-earth.kml+xml; charset=utf-8' }), `${name}.kml`);
};

export function daysBetween(firstDate: Date, secondDate: Date) {
    return Math.floor(Math.abs((firstDate.getTime() - secondDate.getTime()) / (24 * 60 * 60 * 1000)));
}

export function guid() {
    function s4() {
        return Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1);
    }

    return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();
}

export function fileFromEvent(event: any) {
    const target = event.target || event.srcElement;
    if (!(target && target.files)) {
        return;
    }
    return target.files[0];
}

export function isValidImage(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
        const imgElement = new Image();
        imgElement.onload = () => {
            resolve();
        };

        imgElement.onerror = () => {
            reject();
        };

        if (file) {
            imgElement.src = URL.createObjectURL(file);
        } else {
            reject();
        }
    });
}

export interface RouterStateUrl {
    url: string;
    queryParams: Params;
    params: Params;
}

export interface State {
    routerReducer: fromRouter.RouterReducerState<RouterStateUrl>;
}


export type ControlConfigMap<T> = {
    [K in keyof T]: ControlConfig<T[K]>;
};


/**
A mixin that add destroyed subject to any angular class

usage:

```
class MyComponent extends withDestroy() {
    constructor() {
        fromEvent(document, 'click').pipe(
            takeUntil(this.destroyed)
        ).subscribe(x => console.log(x));
    }
}
```

@deprecated use angulars takeUntilDestroy() instead:

```
class MyComponent {
    constructor() {
        fromEvent(document, 'click').pipe(
            takeUntilDestroyed()
        ).subscribe(x => console.log(x));
    }
}
```
*/
export function WithDestroy(Base = class {}) {
    return class extends Base implements OnDestroy {

        protected destroyed = new Subject<void>();

        ngOnDestroy() {
            this.destroyed.next();
            this.destroyed.complete();
        }
    };
}
